import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Description as StatementIcon, Delete as DeleteIcon } from '@mui/icons-material';
import config from '../config';

interface OwnerOption {
  id: string;
  name: string;
}

interface BankStatementUploadProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  // Owner "book" entities to file this statement under (see AccountingPage's entity selector) —
  // passed down as props rather than fetched independently, so a statement uploaded while
  // viewing a specific entity's book defaults to that same entity.
  owners?: OwnerOption[];
  defaultOwnerId?: string | null;
}

interface UploadResult {
  filename: string;
  success: boolean;
  message?: string;
}

// Mirrors InvoiceUpload.tsx's structure — same PDF-only dropzone + one-by-one upload loop.
// Unlike receipts, a failed statement extraction has no manual-entry fallback (retyping dozens
// of transaction rows by hand isn't reasonable), so a failure here is just a terminal error row.
const BankStatementUpload: React.FC<BankStatementUploadProps> = ({ open, onClose, onUploadSuccess, owners = [], defaultOwnerId = null }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [ownerId, setOwnerId] = useState<string | null>(defaultOwnerId);

  // Re-sync to whichever entity's book was selected on the Accounting page each time this
  // dialog opens — so "upload while viewing Nest&Co's book" defaults to Nest&Co automatically.
  React.useEffect(() => {
    if (open) setOwnerId(defaultOwnerId);
  }, [open, defaultOwnerId]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
    setMessage(null);
    setUploadResults([]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
  });

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one PDF file.' });
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setMessage({ type: 'error', text: 'Authentication required. Please log in again.' });
      return;
    }

    setUploading(true);
    setMessage(null);
    const results: UploadResult[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentFileIndex(i);
        const formData = new FormData();
        formData.append('file', file);
        if (ownerId) formData.append('owner_id', ownerId);

        try {
          const response = await fetch(`${config.apiUrl}/api/bank-statements/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
          });

          const data = await response.json();

          if (response.ok) {
            results.push({ filename: file.name, success: true, message: 'Uploaded and extracted' });
          } else if (response.status === 409) {
            results.push({ filename: file.name, success: false, message: '⚠️ Skipped - Duplicate (already exists)' });
          } else {
            results.push({ filename: file.name, success: false, message: data.error || 'Extraction failed' });
          }
        } catch (error) {
          results.push({ filename: file.name, success: false, message: 'Network error' });
        }

      }

      setCurrentFileIndex(null);
      setUploadResults(results);

      const successCount = results.filter(r => r.success).length;
      const skippedCount = results.filter(r => !r.success && r.message?.includes('Duplicate')).length;
      const failCount = results.filter(r => !r.success && !r.message?.includes('Duplicate')).length;

      if (successCount > 0 || skippedCount > 0) {
        const parts = [];
        if (successCount > 0) parts.push(`${successCount} uploaded`);
        if (skippedCount > 0) parts.push(`${skippedCount} skipped (duplicates)`);
        if (failCount > 0) parts.push(`${failCount} failed`);

        setMessage({ type: successCount > 0 ? 'success' : 'warning', text: parts.join(', ') });

        if (failCount === 0) {
          setTimeout(() => {
            onUploadSuccess();
            handleClose();
          }, 1500);
        } else {
          setTimeout(() => {
            onUploadSuccess();
          }, 1500);
        }
      } else {
        setMessage({ type: 'error', text: 'All uploads failed. Please check the files and try again.' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Failed to upload bank statements' });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setMessage(null);
      setUploadResults([]);
      setCurrentFileIndex(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Bank Statement</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {owners.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="bank-statement-owner-label">Book (optional)</InputLabel>
              <Select
                labelId="bank-statement-owner-label"
                label="Book (optional)"
                value={ownerId || ''}
                onChange={e => setOwnerId(e.target.value || null)}
              >
                <MenuItem value="">Unassigned</MenuItem>
                {owners.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <input {...getInputProps()} />
            <StatementIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop statement PDFs here' : 'Drag & drop bank statement PDFs here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to select files
            </Typography>
          </Box>

          {files.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({files.length}):
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      !uploading && (
                        <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {uploading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {files.length > 1 && currentFileIndex !== null
                  ? `Uploading & extracting file ${currentFileIndex + 1} of ${files.length}…`
                  : 'Uploading & extracting…'}
              </Typography>
              {/* Indeterminate, not determinate-by-file-count: the AI extraction step for a single
                  file can itself take up to ~2 minutes (longer statements need more per-transaction
                  reasoning), so a bar that only advances once a whole file finishes would sit at 0%
                  for that entire time and look stuck. */}
              <LinearProgress variant="indeterminate" />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                AI extraction can take up to 2 minutes for longer statements — please don't close this window.
              </Typography>
            </Box>
          )}

          {uploadResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Results:
              </Typography>
              <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                {uploadResults.map((result, index) => {
                  const isDuplicate = result.message?.includes('Duplicate');
                  const color = result.success ? 'success.main' : (isDuplicate ? 'warning.main' : 'error.main');
                  return (
                    <ListItem key={index}>
                      <ListItemText primary={result.filename} secondary={result.message} primaryTypographyProps={{ color }} />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          {uploadResults.length > 0 ? 'Close' : 'Cancel'}
        </Button>
        <Button onClick={handleUpload} variant="contained" disabled={files.length === 0 || uploading}>
          {uploading ? 'Uploading...' : `Upload ${files.length > 0 ? `(${files.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankStatementUpload;
